import React, { useEffect, useState } from "react";
import { LogOut, RefreshCw } from "lucide-react";
import { apiClient } from "@/lib/api";
import Logo from "@/components/Logo";
import { toast } from "sonner";

const StatCard = ({ label, value, testid }) => (
  <div data-testid={testid} className="bg-black border border-zinc-900 p-6">
    <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-3">
      {label}
    </div>
    <div className="font-display text-4xl text-white">{value ?? "—"}</div>
  </div>
);

export default function AdminDashboard() {
  const [me, setMe] = useState(null);
  const [stats, setStats] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [tab, setTab] = useState("memberships");
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [meR, statsR, memR, conR] = await Promise.all([
        apiClient.get("/auth/me", { withCredentials: true }),
        apiClient.get("/admin/stats", { withCredentials: true }),
        apiClient.get("/admin/memberships", { withCredentials: true }),
        apiClient.get("/admin/contacts", { withCredentials: true }),
      ]);
      setMe(meR.data);
      setStats(statsR.data);
      setMemberships(memR.data);
      setContacts(conR.data);
    } catch (e) {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const logout = async () => {
    await apiClient.post("/auth/logout", {}, { withCredentials: true });
    window.location.href = "/admin/login";
  };

  const updateStatus = async (id, status) => {
    try {
      await apiClient.patch(`/admin/memberships/${id}`, { status }, { withCredentials: true });
      toast.success(`Marked ${status}`);
      loadAll();
    } catch {
      toast.error("Update failed");
    }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-black text-white">
      <header className="border-b border-zinc-900 px-6 md:px-12 py-6 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <Logo size="sm" />
          <span className="hidden md:inline font-mono text-[10px] tracking-[0.4em] uppercase text-zinc-500">
            Operations Console
          </span>
        </div>
        <div className="flex items-center gap-4">
          {me && (
            <span className="hidden md:block font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-400">
              {me.email}
            </span>
          )}
          <button
            onClick={loadAll}
            data-testid="admin-refresh"
            className="border border-zinc-800 p-2.5 hover:border-white transition"
            aria-label="Refresh"
          >
            <RefreshCw size={14} />
          </button>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="inline-flex items-center gap-2 border border-zinc-800 px-4 py-2.5 font-mono text-[10px] tracking-[0.3em] uppercase hover:border-white transition"
          >
            <LogOut size={12} />
            Logout
          </button>
        </div>
      </header>

      <main className="px-6 md:px-12 py-10 max-w-[1400px] mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-900 mb-12">
          <StatCard label="Total Members" value={stats?.memberships_total} testid="stat-mem-total" />
          <StatCard label="Pending" value={stats?.memberships_pending} testid="stat-mem-pending" />
          <StatCard label="Active" value={stats?.memberships_active} testid="stat-mem-active" />
          <StatCard label="Contacts" value={stats?.contacts_total} testid="stat-contacts" />
        </div>

        <div className="flex border-b border-zinc-900 mb-8">
          {["memberships", "contacts"].map((t) => (
            <button
              key={t}
              data-testid={`tab-${t}`}
              onClick={() => setTab(t)}
              className={`px-6 py-4 font-mono text-[11px] tracking-[0.3em] uppercase transition ${
                tab === t ? "text-white border-b-2 border-white -mb-px" : "text-zinc-500 hover:text-white"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-20 text-zinc-500 font-mono text-xs tracking-[0.3em] uppercase">
            Loading...
          </div>
        ) : tab === "memberships" ? (
          <div className="border border-zinc-900 overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-zinc-900 bg-zinc-950">
                  {["Date", "Plan", "Name", "Email", "Status", "Actions"].map((h) => (
                    <th key={h} className="px-4 py-4 font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-500">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {memberships.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-zinc-600 font-mono text-xs">
                      No memberships yet
                    </td>
                  </tr>
                ) : (
                  memberships.map((m) => (
                    <tr key={m.id} data-testid={`row-membership-${m.id}`} className="border-b border-zinc-900 hover:bg-zinc-950">
                      <td className="px-4 py-4 font-mono text-[11px] text-zinc-400">
                        {new Date(m.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-4 font-mono text-[11px] uppercase tracking-widest text-white">
                        {m.plan}
                      </td>
                      <td className="px-4 py-4 font-sans text-white">{m.name}</td>
                      <td className="px-4 py-4 font-sans text-zinc-300">{m.email}</td>
                      <td className="px-4 py-4">
                        <span className={`font-mono text-[10px] tracking-[0.2em] uppercase px-2 py-1 border ${
                          m.status === "active"
                            ? "border-white text-white"
                            : m.status === "cancelled"
                            ? "border-zinc-700 text-zinc-500 line-through"
                            : "border-zinc-600 text-zinc-300"
                        }`}>
                          {m.status}
                        </span>
                      </td>
                      <td className="px-4 py-4 space-x-2">
                        {m.status !== "active" && (
                          <button onClick={() => updateStatus(m.id, "active")} className="font-mono text-[10px] tracking-[0.2em] uppercase border border-zinc-700 hover:border-white px-2 py-1">
                            Activate
                          </button>
                        )}
                        {m.status !== "cancelled" && (
                          <button onClick={() => updateStatus(m.id, "cancelled")} className="font-mono text-[10px] tracking-[0.2em] uppercase border border-zinc-700 hover:border-white px-2 py-1">
                            Cancel
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="border border-zinc-900 overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-zinc-900 bg-zinc-950">
                  {["Date", "Name", "Email", "Subject", "Message"].map((h) => (
                    <th key={h} className="px-4 py-4 font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-500">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {contacts.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-12 text-center text-zinc-600 font-mono text-xs">
                      No submissions yet
                    </td>
                  </tr>
                ) : (
                  contacts.map((c) => (
                    <tr key={c.id} data-testid={`row-contact-${c.id}`} className="border-b border-zinc-900 hover:bg-zinc-950 align-top">
                      <td className="px-4 py-4 font-mono text-[11px] text-zinc-400 whitespace-nowrap">
                        {new Date(c.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-4 font-sans text-white">{c.name}</td>
                      <td className="px-4 py-4 font-sans text-zinc-300">{c.email}</td>
                      <td className="px-4 py-4 font-mono text-[11px] text-zinc-300">{c.subject || "—"}</td>
                      <td className="px-4 py-4 text-sm text-zinc-300 max-w-md">{c.message}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
